import { draggable, dropTargetForElements, monitorForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter'
import { setCustomNativeDragPreview } from '@atlaskit/pragmatic-drag-and-drop/element/set-custom-native-drag-preview'
import { captureException } from '@sentry/react'
import { useQueryClient } from '@tanstack/react-query'
import classNames from 'classnames'
import { Fragment, useEffect, useMemo, useRef, useState } from 'react'
import { createRoot } from 'react-dom/client'
import { Responsive, WidthProvider } from 'react-grid-layout'
import { GoFileDirectoryFill } from 'react-icons/go'
import { MdAdd, MdDelete } from 'react-icons/md'
import { useNavigate, useParams, Link } from 'react-router-dom'

import type { ChangeEvent, MouseEvent, PropsWithChildren, ReactElement } from 'react'
import type { ResponsiveProps } from 'react-grid-layout'
import type { Step } from 'react-joyride'
import type { Menu } from '~/components/ActionMenu'
import type { IUserFile } from '~/types/user-file'

import ManageDirectoryModal from './Modals/ManageDirectoryModal'
import UploadFileByIdModal from './Modals/UploadFileByIdModal'
import UploadFileModal from './Modals/UploadFileModal'

import ActionMenu from '~/components/ActionMenu'
import Badge from '~/components/Badge'
import Button from '~/components/Buttons/Button'
import ContentLayout from '~/components/ContentLayout'
import Filters, { FiltersContent } from '~/components/Filters'
import SearchInput from '~/components/Inputs/Basic/SearchInput'
import Layout from '~/components/Layouts/Layout'
import MediaCard from '~/components/MediaCard'
import ModalDialog from '~/components/ModalDialog'
import MwMLoader from '~/components/MwMLoader'
import Onboarding from '~/components/Onboarding'
import Section from '~/components/Section'
import ViewTitle from '~/components/ViewTitle'
import { LOCAL_STORAGE } from '~/const'
import useDocumentTitle from '~/hooks/useDocumentTitle'
import useSingleAndDoubleClick from '~/hooks/useSingleAndDoubleClick'
import {
  keys as userFileQueryKeys,
  useDeleteDirectoryMutation,
  useDeleteUserFileMutation,
  useGetUserFilesByDirectoryQuery,
  useUpdateDirectoryLocationMutation,
  useUpdateFileLocationMutation,
} from '~/queries/user-file'
import { copyText } from '~/utils/navigator'
import { displayToast } from '~/utils/toast'

import './styles.scss'

const onboardingSteps: Step[] = [
  {
    content: 'Welcome to the My Files page! Here you can manage your files and directories.',
    isFixed: true,
    placement: 'center',
    target: '#root',
  },
  {
    content: 'Use the search bar to find your files and directories.',
    target: '.media-search-bar',
  },
  {
    content: 'Click on a file to select it. Use CTRL or CMD to select multiple files.',
    target: '.my-files-card',
  },
  {
    content: 'Click on a directory to select it or double-click to navigate to it.',
    target: '.directory-card',
  },
  {
    content:
      'Use the buttons to upload files, add files by ID, create new directories, and delete files. You can delete multiple files and directories at once.',
    target: '.add-file-by-id-button',
  },
  {
    content:
      'You can drag and drop directories and files to move them around. Drag a file to a directory to move it there. Drag a file or a directory to the header breadcrumb to move it to the directory.',
    isFixed: true,
    placement: 'center',
    target: '#root',
  },
]

const navigationOnboarding: Step[] = [
  {
    content: (
      <>
        <p>You can navigate to a parent directory by clicking on it in the breadcrumb.</p>
        <p>These links are also drop targets so you can drag files and directories directly on it to move them.</p>
      </>
    ),
    target: '.breadcrumb-item',
  },
]

interface IDirectoryItemProps extends IUserFile {
  isSelected: boolean
  onDelete: (fileId: string) => void
  onEdit: (directory: { fileId: string; fileName: string }) => void
  onNavigate: (evt: MouseEvent) => void
  onSelect: (evt: MouseEvent) => void
}

const DirectoryItem = ({
  file_id,
  file_name,
  isSelected,
  onDelete,
  onEdit,
  onNavigate,
  onSelect,
}: IDirectoryItemProps): ReactElement => {
  /** Handler */

  const handleDirectoryClick = useSingleAndDoubleClick(onSelect, onNavigate)

  const menu: Menu = {
    actions: [
      {
        action: () => onEdit({ fileId: file_id, fileName: file_name }),
        label: 'Edit',
      },
      {
        action: () => onDelete(file_id),
        label: 'Delete',
      },
    ],
  }

  /** Render */

  return (
    <div
      className={classNames('directory-card', { 'directory-card--selected': isSelected })}
      onClick={handleDirectoryClick}
    >
      <div className="directory-card__content">
        <GoFileDirectoryFill className="directory-icon" />
        <span className="directory-name" title={file_name}>
          {file_name}
        </span>
      </div>
      <ActionMenu iconDirection="vertical" menu={menu} />
    </div>
  )
}

const DropableItem = ({
  children,
  className,
  fileId,
  fileName,
  path,
}: PropsWithChildren<{ className?: string; fileId: string; fileName: string; path: string }>): ReactElement => {
  const ref = useRef<HTMLDivElement>(null)
  const [isDraggedOver, setIsDraggedOver] = useState(false)

  useEffect(() => {
    const el = ref.current

    return dropTargetForElements({
      canDrop: ({ source }) => fileId !== source.data.location,
      element: el as HTMLElement,
      getData: () => ({ fileName, location: fileId, path }),
      onDragEnter: () => setIsDraggedOver(true),
      onDragLeave: () => setIsDraggedOver(false),
      onDrop: () => setIsDraggedOver(false),
    })
  }, [fileId, fileName, path])

  return (
    <div
      className={classNames(className, {
        'dropable-item--is-hovered': isDraggedOver,
        [`${className}--is-hovered`]: isDraggedOver,
      })}
      ref={ref}
    >
      {children}
    </div>
  )
}

const DraggableItem = ({
  children,
  className,
  fileId,
  fileName,
  isDraggingOtherItem,
  isSelected,
  path,
  selectedFiles,
  type,
}: PropsWithChildren<{
  className?: string
  fileId: string
  fileName: string
  isDraggingOtherItem: boolean
  isSelected: boolean
  path: string
  selectedFiles: string[]
  type: 'file' | 'directory'
}>): ReactElement => {
  const ref = useRef<HTMLDivElement>(null)
  const [dragging, setDragging] = useState<boolean>(false)

  useEffect(() => {
    const el = ref.current

    return draggable({
      element: el as HTMLElement,
      getInitialData: () => ({ fileName, location: fileId, path, type }),
      onDragStart: () => setDragging(true),
      onDrop: () => setDragging(false),
      onGenerateDragPreview: ({ nativeSetDragImage }) => {
        setCustomNativeDragPreview({
          nativeSetDragImage,
          render({ container }) {
            const root = createRoot(container!) // eslint-disable-line
            root.render(
              <div className="draggable-preview">
                <span className="draggable-preview__filename">{fileName}</span>
                {selectedFiles.includes(fileId) && selectedFiles.length > 1 ? (
                  <Badge className="draggable-preview__badge" value={selectedFiles.length} />
                ) : null}
              </div>
            )

            return function cleanup() {
              root.unmount()
            }
          },
        })
      },
    })
  }, [type, fileId, fileName, path, selectedFiles])

  return (
    <div
      className={classNames(className, {
        'draggable-item--is-dragging': dragging || (isDraggingOtherItem && isSelected),
      })}
      ref={ref}
    >
      {children}
    </div>
  )
}

const ReactGridLayout = WidthProvider(Responsive)

const getGridItemPosition = (
  name: string,
  idx: number,
  cols: number
): {
  h: number
  i: string
  w: number
  x: number
  y: number
} => ({
  h: 1,
  i: name,
  w: 1,
  x: idx % cols,
  y: Math.floor(idx / cols),
})

const COMMON_GRID_PROPS: ResponsiveProps = {
  allowOverlap: false,
  breakpoints: { lg: 1380, md: 600 },
  isDraggable: false,
  isResizable: false,
  margin: [20, 20],
}

const Files = (): ReactElement => {
  /** Local state */

  const navigate = useNavigate()

  const { directoryId } = useParams()

  const queryClient = useQueryClient()

  const gridRef = useRef<HTMLDivElement>(null)

  const [isUploadFileModalFileOpen, setIsUploadFileModalFileOpen] = useState(false)

  const [isAddFileByIdModalOpen, setIsAddFileByIdModalOpen] = useState(false)

  const [isDeleteFileModalOpen, setIsDeleteFileModalOpen] = useState(false)

  const [isManageDirectoryModalOpen, setIsManageDirectoryModalOpen] = useState(false)

  const [selectedDirectory, setSelectedDirectory] = useState<{ fileId: string; fileName: string }>()

  const [search, setSearch] = useState('')

  const [selectedFiles, setSelectedFiles] = useState<string[]>([])

  const [currentPath, setCurrentPath] = useState<string>()

  const [isDragging, setIsDragging] = useState(false)

  /** Queries & Mutations */

  const { mutateAsync: deleteUserFile } = useDeleteUserFileMutation()

  const { mutateAsync: deleteDirectory } = useDeleteDirectoryMutation()

  const { mutateAsync: updateFileLocation } = useUpdateFileLocationMutation()

  const { mutateAsync: updateDirectoryLocation } = useUpdateDirectoryLocationMutation()

  const { data: userFiles, isLoading, refetch } = useGetUserFilesByDirectoryQuery(directoryId || 'root')

  const filteredUserFiles = useMemo(
    () => userFiles?.content?.filter(({ file_name }) => file_name.toLowerCase().includes(search.toLowerCase())) || [],
    [search, userFiles]
  )

  /** Effects */

  useDocumentTitle('My files')

  useEffect(() => {
    if (userFiles?.current_directory) {
      setCurrentPath(userFiles.current_directory.path.length ? userFiles.current_directory.path : undefined)
    }
  }, [userFiles?.current_directory])

  useEffect(() => {
    return monitorForElements({
      onDragStart: ({ source }) => {
        setIsDragging(true)

        if (!selectedFiles.includes(source.data.location as string)) {
          setSelectedFiles([])
        }
      },
      onDrop: async ({ location, source }) => {
        setIsDragging(false)

        const destination = location.current.dropTargets[0]

        if (!destination) {
          // if dropped outside of any drop targets
          return
        }

        const destinationLocation = destination.data.location as string
        const destinationPath = destination.data.path as string
        const destinationFileName = destination.data.fileName as string
        const sourceLocation = source.data.location as string
        const sourceFileName = source.data.fileName as string
        const type = source.data.type

        // NOTE: Multiple files dragging
        if (selectedFiles.length > 1) {
          const promises = []
          for (const fileIdToMove of selectedFiles) {
            const fileToMove = filteredUserFiles.find(({ file_id }) => file_id === fileIdToMove)
            if (fileToMove && fileToMove.type === 'directory') {
              const path =
                destinationFileName === 'root'
                  ? '/'
                  : destinationPath === '/'
                    ? `/${destinationFileName}`
                    : `${destinationPath}/${destinationFileName}`
              promises.push(updateDirectoryLocation({ directoryId: fileIdToMove, name: fileToMove.file_name, path }))
            } else if (fileToMove && fileToMove.type === 'file') {
              promises.push(updateFileLocation({ directoryId: destinationLocation, fileId: fileIdToMove }))
            }
          }
          await Promise.all(promises)
        } else {
          // NOTE: Single files dragging
          if (type === 'file') {
            await updateFileLocation({ directoryId: destinationLocation, fileId: sourceLocation })
          } else if (type === 'directory') {
            const path =
              destinationFileName === 'root'
                ? '/'
                : destinationPath === '/'
                  ? `/${destinationFileName}`
                  : `${destinationPath}/${destinationFileName}`
            await updateDirectoryLocation({ directoryId: sourceLocation, name: sourceFileName, path })
          }
        }

        await queryClient.invalidateQueries({ queryKey: userFileQueryKeys.all })
        setSelectedFiles([])
      },
    })
  }, [filteredUserFiles, queryClient, refetch, selectedFiles, updateDirectoryLocation, updateFileLocation])

  /** Handlers */

  const handleSearchChanged = (e: ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)

  const handleToggleUploadFileModal = (isOpen: boolean) => () => setIsUploadFileModalFileOpen(isOpen)

  const handleToggleAddFileModal = (isOpen: boolean) => () => setIsAddFileByIdModalOpen(isOpen)

  const handleToggleDeleteFileModal = (isOpen: boolean) => () => setIsDeleteFileModalOpen(isOpen)

  const handleToggleDirectoryModal = (isOpen: boolean) => () => {
    setSelectedDirectory(undefined)
    setIsManageDirectoryModalOpen(isOpen)
  }

  const handleEditDirectory = (directory: { fileId: string; fileName: string }) => {
    setSelectedDirectory(directory)
    setIsManageDirectoryModalOpen(true)
  }

  const handleDeleteFile = (fileId: string) => {
    setSelectedFiles([fileId])
    setIsDeleteFileModalOpen(true)
  }

  const handleDeleteFileConfirm = async () => {
    try {
      const promises = []
      for (const fileIdToDelete of selectedFiles) {
        if (filteredUserFiles.find(({ file_id }) => file_id === fileIdToDelete)?.type === 'directory') {
          promises.push(deleteDirectory(fileIdToDelete))
        } else {
          promises.push(deleteUserFile(fileIdToDelete))
        }
      }
      await Promise.all(promises)
      displayToast(`${selectedFiles.length > 1 ? 'Files' : 'File'} successfully deleted`, 'success')
      setSelectedFiles([])
      setIsDeleteFileModalOpen(false)
      refetch()
    } catch (e) {
      captureException(e)
      displayToast(`${selectedFiles.length > 1 ? 'Files' : 'File'} could not be deleted`, 'error')
    }
  }

  const handleUploadFileSuccess = () => {
    setIsAddFileByIdModalOpen(false)
    displayToast('File successfully uploaded', 'success')
    refetch()
  }

  const handleManageDirectorySuccess = () => {
    setIsManageDirectoryModalOpen(false)
    displayToast(`Directory successfully ${selectedDirectory ? 'update' : 'create'}`, 'success')
    setSelectedDirectory(undefined)
    refetch()
  }

  const handleSelectFile = (fileId: string) => (evt: MouseEvent) => {
    if (evt.metaKey || evt.ctrlKey) {
      setSelectedFiles((prevSelectedFiles) => {
        const isSelected = prevSelectedFiles.includes(fileId)
        return isSelected
          ? prevSelectedFiles.filter((prevFileId) => prevFileId !== fileId)
          : [...prevSelectedFiles, fileId]
      })
    } else {
      setSelectedFiles([fileId])
    }
  }

  const handleCopyURL = (url: string) => copyText(url)

  const handleDownloadFile = (url: string) => window.open(url, '_blank')

  const handleNavigate = (directoryId: string) => () => {
    setSelectedFiles([])
    navigate(`/my-files/${directoryId}`)
  }

  /** Render */

  const directories = filteredUserFiles
    .filter(({ type }) => type === 'directory')
    .sort((a, b) => a.file_name.localeCompare(b.file_name))

  const files = filteredUserFiles
    .filter(({ type }) => type === 'file')
    .sort((a, b) => a.file_name.localeCompare(b.file_name))

  const breadcrumbs =
    userFiles?.breadcrumb.map(({ directory_id, name, path }, idx) => (
      <Fragment key={`breadcrumb-item-${directory_id}`}>
        <DropableItem className="breadcrumb-item" fileId={directory_id} fileName={name} path={path}>
          <Link key={name} to={`/my-files/${directory_id}`}>
            {name}
          </Link>
        </DropableItem>
        {idx < userFiles.breadcrumb.length - 1 ? <span>/</span> : null}
      </Fragment>
    )) || []

  return (
    <>
      <Onboarding
        continuous
        disableCloseOnEsc
        featureKey={LOCAL_STORAGE.ONBOARDING.FILES}
        key="onboarding-files"
        showProgress
        showSkipButton
        steps={onboardingSteps}
      />
      {currentPath ? (
        <Onboarding
          disableCloseOnEsc
          featureKey={LOCAL_STORAGE.ONBOARDING.FILES_NAVIGATION}
          steps={navigationOnboarding}
        />
      ) : null}
      <ViewTitle className="layout-view-title top-zero my-files-view-title">
        /{breadcrumbs.length ? <>{breadcrumbs}/</> : null}
        {userFiles && userFiles.current_directory.name !== 'root' ? (
          <div className="breadcrumb-item">{userFiles.current_directory.name}</div>
        ) : null}
      </ViewTitle>
      <Filters className="my-files-filters">
        <FiltersContent>
          <SearchInput className="media-search-bar" disabled={isLoading} onChange={handleSearchChanged} />
          <Button onClick={handleToggleUploadFileModal(true)}>
            <MdAdd color="#fff" size={16} />
            Upload file
          </Button>
          <Button className="add-file-by-id-button" onClick={handleToggleAddFileModal(true)}>
            <MdAdd color="#fff" size={16} />
            Add file by ID
          </Button>
          <Button onClick={handleToggleDirectoryModal(true)}>
            <MdAdd color="#fff" size={16} />
            New directory
          </Button>
          {selectedFiles.length > 0 ? (
            <Button onClick={handleToggleDeleteFileModal(true)}>
              <MdDelete color="#fff" size={16} />
            </Button>
          ) : null}
        </FiltersContent>
      </Filters>
      <Layout className="my-files-view" withPadding>
        <ContentLayout className={classNames({ 'is-loading': isLoading })}>
          {isLoading ? <MwMLoader category="item" /> : null}
          {directories.length ? (
            <Section title="Directories">
              <div className="directories-container">
                {directories.map((directory, idx) => (
                  <DraggableItem
                    className="directory-card-container"
                    fileId={directory.file_id}
                    fileName={directory.file_name}
                    isDraggingOtherItem={isDragging}
                    isSelected={selectedFiles.includes(directory.file_id)}
                    key={`directory-${directory.file_id}`}
                    path={directory.path}
                    selectedFiles={selectedFiles}
                    type="directory"
                  >
                    <DropableItem
                      className="directory-card-container__dropable"
                      fileId={directory.file_id}
                      fileName={directory.file_name}
                      path={directory.path}
                    >
                      <DirectoryItem
                        {...directory}
                        isSelected={selectedFiles.includes(directory.file_id)}
                        key={idx}
                        onDelete={handleDeleteFile}
                        onEdit={handleEditDirectory}
                        onNavigate={handleNavigate(directory.file_id)}
                        onSelect={handleSelectFile(directory.file_id)}
                      />
                    </DropableItem>
                  </DraggableItem>
                ))}
              </div>
            </Section>
          ) : null}
          {files.length ? (
            <Section ref={gridRef} title="Files">
              <ReactGridLayout
                {...COMMON_GRID_PROPS}
                cols={{ lg: 5, md: 5 }}
                containerPadding={[0, 0]}
                margin={[20, 10]}
                rowHeight={250}
              >
                {files.map(({ download_url, file_id, file_name, mime_type, path }, idx) => (
                  <div
                    data-grid={getGridItemPosition(`item-${idx}`, idx, 5)}
                    key={`file-${file_id}`}
                    onClick={handleSelectFile(file_id)}
                  >
                    <DraggableItem
                      className="my-files-card-container"
                      fileId={file_id}
                      fileName={file_name}
                      isDraggingOtherItem={isDragging}
                      isSelected={selectedFiles.includes(file_id)}
                      path={path}
                      selectedFiles={selectedFiles}
                      type="file"
                    >
                      <MediaCard
                        actionMenu={{
                          actions: [
                            {
                              action: () => handleDeleteFile(file_id),
                              label: 'Delete',
                            },
                            {
                              action: () => handleCopyURL(download_url),
                              label: 'Copy URL',
                            },
                            {
                              action: () => handleDownloadFile(download_url),
                              label: 'Download',
                            },
                          ],
                        }}
                        className={classNames('my-files-card', {
                          'my-files-card--selected': selectedFiles.includes(file_id),
                        })}
                        downloadUrl={download_url}
                        fileId={file_id}
                        fileName={file_name}
                        mimeType={mime_type}
                      />
                    </DraggableItem>
                  </div>
                ))}
              </ReactGridLayout>
            </Section>
          ) : null}
        </ContentLayout>
      </Layout>
      <ModalDialog
        isOpen={isDeleteFileModalOpen}
        mainText={`Do you really want to delete ${selectedFiles.length > 1 ? 'these elements' : 'this element'}?`}
        onClose={handleToggleDeleteFileModal(false)}
        onConfirm={handleDeleteFileConfirm}
        subText={
          directories.some(({ file_id }) => selectedFiles.includes(file_id))
            ? 'Be careful! This will delete all the content'
            : undefined
        }
      />
      {userFiles ? (
        <>
          <UploadFileModal
            directoryId={userFiles.current_directory.directory_id}
            isOpen={isUploadFileModalFileOpen}
            onCloseModal={handleToggleUploadFileModal(false)}
            refetch={refetch}
          />
          <UploadFileByIdModal
            directoryId={userFiles.current_directory.directory_id}
            isOpen={isAddFileByIdModalOpen}
            onCloseModal={handleToggleAddFileModal(false)}
            onSuccess={handleUploadFileSuccess}
          />
          <ManageDirectoryModal
            directoryId={userFiles.current_directory.directory_id}
            directoryName={userFiles.current_directory.name}
            initialDirectory={selectedDirectory}
            isOpen={isManageDirectoryModalOpen}
            onCloseModal={handleToggleDirectoryModal(false)}
            onSuccess={handleManageDirectorySuccess}
            path={currentPath}
          />
        </>
      ) : null}
    </>
  )
}

export default Files
